import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Mail, Camera, Sparkles, MessageSquare, Send, Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { AuthModal } from "@/components/AuthModal";
import { sounds } from "@/lib/sounds";

const CORK = "https://images.unsplash.com/photo-1558051815-0f18e64e6280?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600";

const heroNotes = [
  { t: "Your", c: "note-yellow", r: -6, x: 0 },
  { t: "inbox,", c: "note-pink", r: 4, x: 0 },
  { t: "un-", c: "note-blue", r: -3, x: 0 },
  { t: "boxed", c: "note-green", r: 5, x: 0 },
];

export default function Landing() {
  const navigate = useNavigate();
  const { user, startDemo } = useAuth();
  const [showAuth, setShowAuth] = useState(false);

  useEffect(() => { if (user) navigate("/board", { replace: true }); }, [user, navigate]);
  useEffect(() => { const ref = new URLSearchParams(window.location.search).get("ref"); if (ref) localStorage.setItem("pm_ref", ref); }, []);

  const loginGoogle = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/board";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const tryDemo = async () => {
    sounds.peel();
    await startDemo();
    navigate("/board");
  };

  return (
    <div className="min-h-screen relative grain overflow-hidden" style={{ background: `linear-gradient(180deg, rgba(90,64,51,.2), rgba(90,64,51,.5)), url('${CORK}')`, backgroundSize: "cover", backgroundPosition: "center" }}>
      <div className="absolute inset-0 pointer-events-none" style={{ boxShadow: "inset 0 0 0 14px #5c4033, inset 0 0 0 16px rgba(0,0,0,.3)" }} />

      <header className="relative z-10 flex items-center justify-between px-6 sm:px-12 py-6">
        <div className="flex items-center gap-2">
          <span className="thumbtack !relative !top-0 !left-0 !translate-x-0" />
          <span className="font-display font-black text-2xl text-[#fdf6e3] tracking-tight" style={{ textShadow: "0 2px 6px rgba(0,0,0,.4)" }}>Pin a Note</span>
        </div>
        <div className="flex items-center gap-3">
          <button data-testid="landing-demo-btn" onClick={tryDemo} className="font-display font-bold text-sm text-[#fdf6e3] hover:underline">Try the demo</button>
          <Button data-testid="landing-signin-btn" onClick={() => setShowAuth(true)} className="rounded-full bg-[#fdf6e3] text-[#3a2f22] hover:bg-white font-display font-bold px-5">Sign in</Button>
        </div>
      </header>

      <section className="relative z-10 px-6 sm:px-12 pt-8 pb-20 max-w-6xl">
        <div className="flex flex-wrap gap-3 mb-8">
          {heroNotes.map((n, i) => (
            <motion.div key={n.t} initial={{ y: -40, opacity: 0, rotate: 0 }} animate={{ y: 0, opacity: 1, rotate: n.r }}
              transition={{ delay: 0.1 * i, type: "spring", stiffness: 120, damping: 10 }}
              className={`${n.c} paper-shadow relative px-5 py-4 rounded-[3px]`}>
              <span className="thumbtack" />
              <span className="font-note text-3xl sm:text-5xl font-bold text-[#2b2620]">{n.t}</span>
            </motion.div>
          ))}
        </div>

        <motion.h1 initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.5 }}
          className="font-display font-black text-4xl sm:text-5xl lg:text-6xl leading-none text-[#fdf6e3] max-w-2xl" style={{ textShadow: "0 3px 10px rgba(0,0,0,.45)" }}>
          Your messages, as sticky notes.
        </motion.h1>
        <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.7 }}
          className="mt-5 text-lg text-[#f3e7cf] max-w-xl font-medium">
          Your email, texts, and Telegram messages land on a warm cork board. Pin them, drag to reorder, crumple when done — and pin your polaroids anywhere.
        </motion.p>

        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.8 }} className="mt-5 flex flex-wrap items-center gap-2">
          {[{ icon: Mail, t: "Email" }, { icon: MessageSquare, t: "SMS" }, { icon: Send, t: "Telegram" }].map((c) => (
            <span key={c.t} data-testid={`channel-badge-${c.t.toLowerCase()}`} className="inline-flex items-center gap-1.5 rounded-full bg-[#fdf6e3]/15 border border-[#fdf6e3]/25 backdrop-blur px-3 py-1 text-sm font-display font-bold text-[#fdf6e3]">
              <c.icon className="w-3.5 h-3.5" /> {c.t}
            </span>
          ))}
        </motion.div>

        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.9 }} className="mt-8 flex flex-wrap gap-3">
          <Button data-testid="hero-google-btn" onClick={() => setShowAuth(true)} className="rounded-full h-12 px-7 bg-[#ff3b30] hover:bg-[#e0271d] text-white font-display font-bold text-base">
            Sign up free
          </Button>
          <Button data-testid="hero-demo-btn" onClick={tryDemo} variant="outline" className="rounded-full h-12 px-7 bg-[#fdf6e3]/90 hover:bg-white border-none text-[#3a2f22] font-display font-bold text-base">
            Peek at a live board →
          </Button>
        </motion.div>
      </section>

      <section className="relative z-10 bg-[#fbf6ec] grain rounded-t-[2rem] px-6 sm:px-12 py-16">
        <p className="tag-label text-[#a0703c]">How it works</p>
        <h2 className="font-display font-bold text-2xl sm:text-3xl lg:text-4xl mt-2 mb-10 text-[#2b2620] max-w-xl">Four steps to a board that feels alive.</h2>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          {[
            { icon: Mail, t: "1. Claim your address", d: "Get handle@pinanote.app. Anything sent there becomes a note." },
            { icon: Sparkles, t: "2. Pick your board", d: "Five hand-crafted themes. Cozy Cork is the cozy default." },
            { icon: Camera, t: "3. Pin your photos", d: "Drop images as tilted polaroids with tape and captions." },
            { icon: Check, t: "4. Crumple when done", d: "Fling a note right and hear the satisfying paper crunch." },
          ].map((f) => (
            <div key={f.t} className="bg-white rounded-2xl p-6 paper-shadow">
              <f.icon className="w-7 h-7 text-[#ff3b30]" strokeWidth={2.2} />
              <h3 className="font-display font-bold text-lg mt-3 text-[#2b2620]">{f.t}</h3>
              <p className="text-sm text-[#6b6155] mt-1 leading-relaxed">{f.d}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="relative z-10 bg-[#fbf6ec] px-6 sm:px-12 pb-20">
        <p className="tag-label text-[#a0703c]">Pricing</p>
        <h2 className="font-display font-bold text-2xl sm:text-3xl lg:text-4xl mt-2 mb-10 text-[#2b2620]">Start free. Upgrade for the fun stuff.</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-5xl">
          {[
            { n: "Free", p: "$0", f: ["Email-in address", "5 preset themes", "100 notes, 10 polaroids", "Basic sounds"], hot: false },
            { n: "Pro", p: "$4.99/mo", f: ["Personal SMS number", "Unlimited notes & polaroids", "Custom Board Studio", "All sound packs, quiet hours"], hot: true },
            { n: "Family", p: "$8.99/mo", f: ["Everything in Pro", "Shared boards for 5", "Great for households", "Priority support"], hot: false },
          ].map((t) => (
            <div key={t.n} className={`rounded-2xl p-7 relative ${t.hot ? "bg-[#2b2620] text-[#fdf6e3]" : "bg-white text-[#2b2620]"} paper-shadow`}>
              {t.hot && <span className="absolute -top-3 left-6 bg-[#ff3b30] text-white tag-label px-3 py-1 rounded-full">7-day trial</span>}
              <h3 className="font-display font-black text-xl">{t.n}</h3>
              <p className="font-display font-black text-3xl mt-1">{t.p}</p>
              <ul className="mt-5 space-y-2">
                {t.f.map((x) => <li key={x} className="flex gap-2 text-sm"><Check className="w-4 h-4 mt-0.5 shrink-0 text-[#ff3b30]" />{x}</li>)}
              </ul>
              <Button
                data-testid={`pricing-cta-${t.n.toLowerCase()}`}
                onClick={() => setShowAuth(true)}
                className={`w-full mt-6 rounded-full font-display font-bold h-11 ${t.hot ? "bg-[#ff3b30] hover:bg-[#e0271d] text-white" : "bg-[#2b2620] hover:bg-black text-[#fdf6e3]"}`}
              >
                {t.n === "Free" ? "Start free" : t.n === "Pro" ? "Start 7-day trial" : "Choose Family"}
              </Button>
            </div>
          ))}
        </div>
        <p className="text-xs text-[#a89a86] mt-8">US & Canada at launch • PWA install for mobile • Public profiles are opt-in.</p>
      </section>

      <footer className="relative z-10 bg-[#2b2620] text-[#fdf6e3] px-6 sm:px-12 py-10">
        <div className="max-w-6xl flex flex-col sm:flex-row sm:items-center sm:justify-between gap-6">
          <div>
            <div className="flex items-center gap-2">
              <span className="thumbtack !relative !top-0 !left-0 !translate-x-0" />
              <span className="font-display font-black text-xl tracking-tight">Pin a Note</span>
            </div>
            <p className="text-sm text-[#cbbfa8] mt-2 max-w-xs">Your inbox, un-boxed. Email, SMS & Telegram as sticky notes on a cork board you actually enjoy.</p>
          </div>
          <div className="flex flex-wrap items-center gap-4 text-sm font-display font-bold">
            <button data-testid="footer-demo-btn" onClick={tryDemo} className="hover:text-[#ff3b30] transition-colors">Try the demo</button>
            <button data-testid="footer-signin-btn" onClick={() => setShowAuth(true)} className="hover:text-[#ff3b30] transition-colors">Sign in</button>
            <a href="#" className="hover:text-[#ff3b30] transition-colors">Privacy</a>
          </div>
        </div>
        <p className="text-xs text-[#9b8f7a] mt-8">© {new Date().getFullYear()} Pin a Note. Made for people who miss their fridge door.</p>
      </footer>

      <AuthModal open={showAuth} onClose={() => setShowAuth(false)} onSuccess={() => { setShowAuth(false); navigate("/board"); }} onGoogle={loginGoogle} />
    </div>
  );
}
